import { useMutation, useQuery } from "@apollo/client";
import { Box, Skeleton } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useFeatureFlags } from "hooks/useFeatureFlags";

import { ATTACH_SHIPMENT_MANIFEST_MUTATION, MANIFEST_LINK_REFETCH_QUERIES } from "../Shipments/api";
import { MANIFEST_SHIPMENT_LINK_QUERY } from "./api";
import { CandidateShipmentList, LinkedShipmentSummary } from "./ManifestShipmentSections";
import { SectionCard } from "./sections/SectionCard";
import { SectionHeader } from "./sections/SectionHeader";

/**
 * The shipment side of a manifest: the containers whose waste it carried, or —
 * when nothing is attached — the open shipments it might belong to.
 *
 * This is the manual-link fallback the auto-matcher hands off to. Auto-match only
 * acts when one manifest line is corroborated by a shipment line's own waste
 * stream; everything short of that lands here for a person to decide, which is
 * the right trade when the alternative is writing a wrong link into the
 * compliance record.
 */
export function ManifestLinkedShipmentCard({
  processedManifestId,
  tenantId,
}: {
  processedManifestId: string;
  tenantId: string | undefined;
}) {
  const alerts = useAlerts();
  const { featureFlags } = useFeatureFlags();
  const enabled = !!featureFlags[FeatureFlagKey.ContainerTracking];

  const { data, loading } = useQuery(MANIFEST_SHIPMENT_LINK_QUERY, {
    variables: { search: `tenantId:${tenantId} id:${processedManifestId}` },
    skip: !enabled || !tenantId,
  });

  const [attach, { loading: attaching }] = useMutation(ATTACH_SHIPMENT_MANIFEST_MUTATION, {
    refetchQueries: [...MANIFEST_LINK_REFETCH_QUERIES, "ManifestShipmentLink"],
  });

  if (!enabled) {
    return null;
  }

  const manifest = data?.processedManifests?.items?.[0];
  const linkedShipment = manifest?.linkedShipment;
  const containers = manifest?.linkedContainers ?? [];
  const candidates = manifest?.candidateShipments ?? [];

  if (loading && !manifest) {
    return (
      <Box>
        <SectionCard>
          <SectionHeader title="Shipment & containers" />
          <Skeleton variant="rounded" height={80} />
        </SectionCard>
      </Box>
    );
  }

  // Nothing attached and nothing plausible to offer. Rendering an empty card here
  // would put a container-tracking section on every manifest of a tenant that
  // happens to have the flag but packages shipments at other facilities.
  if (!linkedShipment && candidates.length === 0) {
    return null;
  }

  const handleLink = async (shipmentId: string) => {
    try {
      await attach({
        variables: { input: { shipmentId, processedManifestId, signed: false } },
      });
      alerts.success("Manifest linked to shipment.");
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to link the shipment.");
    }
  };

  return (
    <Box>
      <SectionCard>
        <SectionHeader title="Shipment & containers" />
        {linkedShipment ? (
          <LinkedShipmentSummary
            shipment={linkedShipment}
            containers={containers}
            tenantId={tenantId}
          />
        ) : (
          <CandidateShipmentList
            candidates={candidates}
            tenantId={tenantId}
            attaching={attaching}
            onLink={handleLink}
          />
        )}
      </SectionCard>
    </Box>
  );
}
